/**
 * backgrounds.js
 * DIGIDMN — Background animation blocks
 *
 * Architecture:
 *   - BgUtils: shared utility functions
 *   - BackgroundBlock: base lifecycle class (setup, start/stop, resize, visibility)
 *   - Named animation blocks: one IIFE per dedicated block (e.g. v2-bg-gradient-mesh)
 *   - BG_ANIMATIONS registry: named functions for css_motion backgrounds in
 *     v2-cards and v2-service-spotlight (selected via ACF "JS Background Type")
 *   - BG_BLOCKS dispatcher: runs the registry entries for css_motion blocks
 *
 * Adding a new named animation (for css_motion selector):
 *   1. Add a function to BG_ANIMATIONS with a unique key
 *   2. Add the key as a choice in the ACF js_background_type field on each block
 *
 * Adding a new dedicated background block:
 *   1. Create the block, partial, and SCSS following the v2-bg-gradient-mesh pattern
 *   2. Add a new IIFE at the bottom of this file
 *   3. Add the block's section selector to BG_BLOCKS if it also supports css_motion
 */

'use strict';

// ─────────────────────────────────────────────────────────────────────────────
// Shared utilities
// ─────────────────────────────────────────────────────────────────────────────

var BgUtils = (function () {

    function clamp(val, min, max) {
        return Math.min(Math.max(val, min), max);
    }

    function lerp(a, b, t) {
        return a + (b - a) * t;
    }

    function mapRange(val, inMin, inMax, outMin, outMax) {
        return outMin + (outMax - outMin) * ((val - inMin) / (inMax - inMin));
    }

    function randomRange(min, max) {
        return min + Math.random() * (max - min);
    }

    /**
     * Read data-bg-* attributes from an element and merge with defaults.
     */
    function getDataOptions(el, defaults) {
        var opts    = Object.assign({}, defaults);
        var dataset = el.dataset;

        if (dataset.bgPalette    !== undefined) opts.palette    = dataset.bgPalette;
        if (dataset.bgIntensity  !== undefined) opts.intensity  = dataset.bgIntensity;
        if (dataset.bgSpeed      !== undefined) opts.speed      = parseFloat(dataset.bgSpeed);
        if (dataset.bgOverlay    !== undefined) opts.overlay    = parseFloat(dataset.bgOverlay);
        if (dataset.bgMobile     !== undefined) opts.mobileMode = dataset.bgMobile;

        return opts;
    }

    /**
     * Find the canvas inside parent (or create one), return { canvas, ctx }.
     */
    function createCanvas(parent) {
        var canvas = parent.querySelector('canvas');
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.setAttribute('aria-hidden', 'true');
            parent.insertBefore(canvas, parent.firstChild);
        }
        return { canvas: canvas, ctx: canvas.getContext('2d') };
    }

    /**
     * Resize a canvas to match its parent's CSS dimensions, accounting for DPR.
     * Caps DPR at 2 to avoid performance issues on high-density screens.
     * Returns { w, h } in CSS pixels (use these for drawing coordinates).
     */
    function resizeCanvas(canvas, parent) {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w   = parent.offsetWidth;
        var h   = parent.offsetHeight;
        canvas.width        = w * dpr;
        canvas.height       = h * dpr;
        canvas.style.width  = w + 'px';
        canvas.style.height = h + 'px';
        canvas.getContext('2d').scale(dpr, dpr);
        return { w: w, h: h };
    }

    function prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function isMobile() {
        return window.innerWidth < 768;
    }

    /**
     * Pause the animation when the element leaves the viewport,
     * resume when it returns. Returns a stop function.
     */
    function watchVisibility(el, onEnter, onLeave) {
        if (!('IntersectionObserver' in window)) {
            onEnter();
            return function () {};
        }
        var obs = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) { onEnter(); }
                else if (onLeave) { onLeave(); }
            });
        }, { threshold: 0.05 });
        obs.observe(el);
        return function () { obs.disconnect(); };
    }

    return {
        clamp:                clamp,
        lerp:                 lerp,
        mapRange:             mapRange,
        randomRange:          randomRange,
        getDataOptions:       getDataOptions,
        createCanvas:         createCanvas,
        resizeCanvas:         resizeCanvas,
        prefersReducedMotion: prefersReducedMotion,
        isMobile:             isMobile,
        watchVisibility:      watchVisibility
    };

})();


// ─────────────────────────────────────────────────────────────────────────────
// BackgroundBlock — base lifecycle class
// ─────────────────────────────────────────────────────────────────────────────

function BackgroundBlock(el, defaults) {
    this.el             = el;
    this.opts           = BgUtils.getDataOptions(el, defaults || {});
    this.raf            = null;
    this.running        = false;
    this.simplified     = false;
    this.stopWatcher    = null;
    this.canvas         = null;
    this.ctx            = null;
    this.w              = 0;
    this.h              = 0;
    this._resizeHandler = null;
}

BackgroundBlock.prototype.setupCanvas = function () {
    var result  = BgUtils.createCanvas(this.el);
    this.canvas = result.canvas;
    this.ctx    = result.ctx;
    var dims    = BgUtils.resizeCanvas(this.canvas, this.el);
    this.w      = dims.w;
    this.h      = dims.h;
};

BackgroundBlock.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._tick();
};

BackgroundBlock.prototype.stop = function () {
    this.running = false;
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
};

BackgroundBlock.prototype._tick = function () {
    if (!this.running) return;
    this.draw();
    var self = this;
    this.raf = requestAnimationFrame(function () { self._tick(); });
};

// Subclasses override this
BackgroundBlock.prototype.draw = function () {};

BackgroundBlock.prototype.resize = function () {
    var dims = BgUtils.resizeCanvas(this.canvas, this.el);
    this.w   = dims.w;
    this.h   = dims.h;
    if (this.onResize) { this.onResize(); }
};

BackgroundBlock.prototype.destroy = function () {
    this.stop();
    if (this.stopWatcher)    { this.stopWatcher(); }
    if (this._resizeHandler) { window.removeEventListener('resize', this._resizeHandler); }
};

BackgroundBlock.prototype.init = function () {
    var self = this;

    if (BgUtils.prefersReducedMotion()) {
        var c = this.el.querySelector('canvas');
        if (c) { c.style.display = 'none'; }
        return;
    }

    var mob = this.opts.mobileMode || 'simplify';

    if (BgUtils.isMobile() && mob === 'disable') {
        var c2 = this.el.querySelector('canvas');
        if (c2) { c2.style.display = 'none'; }
        return;
    }

    this.simplified = BgUtils.isMobile() && mob === 'simplify';

    this.setupCanvas();

    this.stopWatcher = BgUtils.watchVisibility(
        this.el,
        function () { self.start(); },
        function () { self.stop(); }
    );

    var resizeTimer;
    this._resizeHandler = function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            self.simplified = BgUtils.isMobile() && (self.opts.mobileMode === 'simplify');
            self.resize();
        }, 150);
    };
    window.addEventListener('resize', this._resizeHandler, { passive: true });
};


// ─────────────────────────────────────────────────────────────────────────────
// Gradient Mesh — shared data (used by both the dedicated block and the registry)
// ─────────────────────────────────────────────────────────────────────────────

var GRADIENT_MESH_PALETTES = {
    agency: [[193, 8, 125],  [60, 137, 201],  [118, 35, 120]],
    ocean:  [[6, 182, 212],  [56, 189, 248],  [99, 102, 241]],
    ember:  [[239, 68, 68],  [249, 115, 22],  [236, 72, 153]],
    mono:   [[50, 65, 120],  [70, 80, 140],   [40, 55, 100]]
};

var GRADIENT_MESH_INTENSITY = { low: 0.62, medium: 1.0, high: 1.45 };


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-gradient-mesh — dedicated block (full lifecycle via BackgroundBlock)
// ─────────────────────────────────────────────────────────────────────────────

(function () {

    var DEFAULTS = {
        palette:    'agency',
        intensity:  'medium',
        speed:      4,
        mobileMode: 'simplify'
    };

    function GradientMesh(el) {
        BackgroundBlock.call(this, el, DEFAULTS);
        this.t = 0;
    }

    GradientMesh.prototype = Object.create(BackgroundBlock.prototype);
    GradientMesh.prototype.constructor = GradientMesh;

    GradientMesh.prototype.draw = function () {
        var ctx   = this.ctx;
        var W     = this.w;
        var H     = this.h;
        var opts  = this.opts;
        var pal   = GRADIENT_MESH_PALETTES[opts.palette]   || GRADIENT_MESH_PALETTES.agency;
        var scale = GRADIENT_MESH_INTENSITY[opts.intensity] || 1.0;
        var spd   = BgUtils.clamp(opts.speed, 1, 10) / 10;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#05050e';
        ctx.fillRect(0, 0, W, H);

        if (this.simplified) {
            var col = pal[0];
            var cx  = W * (0.5 + Math.sin(this.t * spd * 0.15) * 0.15);
            var cy  = H * (0.5 + Math.cos(this.t * spd * 0.12) * 0.12);
            var r   = Math.min(W, H) * 0.55;
            var g   = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
            g.addColorStop(0,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.22)');
            g.addColorStop(0.6, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.06)');
            g.addColorStop(1,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, W, H);
        } else {
            var t = this.t;
            pal.forEach(function (col, i) {
                var cx = W * (0.18 + 0.64 * (0.5 + 0.5 * Math.sin(t * spd * 0.5 + i * 2.09)));
                var cy = H * (0.15 + 0.70 * (0.5 + 0.5 * Math.cos(t * spd * 0.4 + i * 1.71)));
                var r  = Math.min(W, H) * (0.32 + i * 0.09) * scale;
                var g  = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
                g.addColorStop(0,    'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.40)');
                g.addColorStop(0.55, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.10)');
                g.addColorStop(1,    'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, W, H);
            });
        }

        this.t += 0.016;
    };

    function initV2BgGradientMesh() {
        var blocks = document.querySelectorAll('[data-bg-block="gradient-mesh"]');
        if (!blocks.length) return;

        blocks.forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new GradientMesh(el).init();
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initV2BgGradientMesh);
    } else {
        initV2BgGradientMesh();
    }

})();


// ─────────────────────────────────────────────────────────────────────────────
// BG_ANIMATIONS registry
// Named animation functions for the css_motion background option in
// v2-cards and v2-service-spotlight. Each receives (canvas, section).
//
// Adding a new type:
//   1. Add an entry below with a unique key
//   2. Add the key as a choice in the ACF "JS Background Type" field on each block
// ─────────────────────────────────────────────────────────────────────────────

var BG_ANIMATIONS = {};

// ── Particle Network ─────────────────────────────────────────────────────────
BG_ANIMATIONS['particle_network'] = function (canvas, section) {
    var ctx   = canvas.getContext('2d');
    var nodes = [];
    var t     = 0;

    function resize() {
        canvas.width  = section.offsetWidth;
        canvas.height = section.offsetHeight;
    }

    function initNodes() {
        nodes = [];
        for (var i = 0; i < 28; i++) {
            nodes.push({
                x:  Math.random() * canvas.width,
                y:  Math.random() * canvas.height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                r:  1.4 + Math.random() * 1.8
            });
        }
    }

    function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        t += 0.006;

        nodes.forEach(function (n) {
            n.x += n.vx;
            n.y += n.vy;
            if (n.x < 0 || n.x > canvas.width)  n.vx *= -1;
            if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        });

        for (var i = 0; i < nodes.length; i++) {
            for (var j = i + 1; j < nodes.length; j++) {
                var dx = nodes[i].x - nodes[j].x;
                var dy = nodes[i].y - nodes[j].y;
                var d  = Math.sqrt(dx * dx + dy * dy);
                if (d < 170) {
                    ctx.beginPath();
                    ctx.strokeStyle = 'rgba(193,8,125,' + (0.22 * (1 - d / 170)) + ')';
                    ctx.lineWidth   = 0.7;
                    ctx.moveTo(nodes[i].x, nodes[i].y);
                    ctx.lineTo(nodes[j].x, nodes[j].y);
                    ctx.stroke();
                }
            }
        }

        nodes.forEach(function (n) {
            ctx.beginPath();
            ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(193,8,125,0.65)';
            ctx.fill();
        });

        var gx1 = canvas.width * 0.72 + Math.sin(t * 0.7) * 90;
        var gy1 = canvas.height * 0.22 + Math.cos(t * 0.5) * 70;
        var g1  = ctx.createRadialGradient(gx1, gy1, 0, gx1, gy1, 300);
        g1.addColorStop(0, 'rgba(193,8,125,0.14)');
        g1.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g1;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        var gx2 = canvas.width * 0.22 + Math.cos(t * 0.4) * 80;
        var gy2 = canvas.height * 0.75 + Math.sin(t * 0.6) * 60;
        var g2  = ctx.createRadialGradient(gx2, gy2, 0, gx2, gy2, 260);
        g2.addColorStop(0, 'rgba(60,137,201,0.1)');
        g2.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        requestAnimationFrame(draw);
    }

    resize();
    initNodes();
    draw();

    window.addEventListener('resize', function () { resize(); initNodes(); });
};

// ── Aurora Drift ─────────────────────────────────────────────────────────────
BG_ANIMATIONS['aurora_drift'] = function (canvas, section) {
    var ctx = canvas.getContext('2d');
    var t   = 0;

    function resize() {
        canvas.width  = section.offsetWidth;
        canvas.height = section.offsetHeight;
    }

    function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        t += 0.004;

        var W = canvas.width;
        var H = canvas.height;
        var S = Math.min(W, H);

        var x1 = W * 0.75 + Math.sin(t * 0.7) * W * 0.12;
        var y1 = H * 0.28 + Math.cos(t * 0.5) * H * 0.18;
        var g1 = ctx.createRadialGradient(x1, y1, 0, x1, y1, S * 0.55);
        g1.addColorStop(0,   'rgba(193,8,125,0.18)');
        g1.addColorStop(0.5, 'rgba(193,8,125,0.06)');
        g1.addColorStop(1,   'rgba(0,0,0,0)');
        ctx.fillStyle = g1;
        ctx.fillRect(0, 0, W, H);

        var x2 = W * 0.22 + Math.cos(t * 0.4) * W * 0.10;
        var y2 = H * 0.70 + Math.sin(t * 0.6) * H * 0.15;
        var g2 = ctx.createRadialGradient(x2, y2, 0, x2, y2, S * 0.50);
        g2.addColorStop(0,   'rgba(60,137,201,0.14)');
        g2.addColorStop(0.5, 'rgba(60,137,201,0.04)');
        g2.addColorStop(1,   'rgba(0,0,0,0)');
        ctx.fillStyle = g2;
        ctx.fillRect(0, 0, W, H);

        var x3 = W * 0.50 + Math.sin(t * 0.3 + 1.2) * W * 0.18;
        var y3 = H * 0.45 + Math.cos(t * 0.45 + 0.8) * H * 0.20;
        var g3 = ctx.createRadialGradient(x3, y3, 0, x3, y3, S * 0.38);
        g3.addColorStop(0, 'rgba(118,35,120,0.12)');
        g3.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g3;
        ctx.fillRect(0, 0, W, H);

        requestAnimationFrame(draw);
    }

    resize();
    draw();

    window.addEventListener('resize', resize);
};

// ── Gradient Mesh (lightweight — uses default agency palette) ─────────────────
BG_ANIMATIONS['gradient_mesh'] = function (canvas, section) {
    var ctx   = canvas.getContext('2d');
    var pal   = GRADIENT_MESH_PALETTES['agency'];
    var scale = GRADIENT_MESH_INTENSITY['medium'];
    var spd   = 0.4; // speed 4/10
    var t     = 0;

    function resize() {
        canvas.width  = section.offsetWidth;
        canvas.height = section.offsetHeight;
    }

    function draw() {
        var W = canvas.width;
        var H = canvas.height;
        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#05050e';
        ctx.fillRect(0, 0, W, H);

        pal.forEach(function (col, i) {
            var cx = W * (0.18 + 0.64 * (0.5 + 0.5 * Math.sin(t * spd * 0.5 + i * 2.09)));
            var cy = H * (0.15 + 0.70 * (0.5 + 0.5 * Math.cos(t * spd * 0.4 + i * 1.71)));
            var r  = Math.min(W, H) * (0.32 + i * 0.09) * scale;
            var g  = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
            g.addColorStop(0,    'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.40)');
            g.addColorStop(0.55, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.10)');
            g.addColorStop(1,    'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, W, H);
        });

        t += 0.016;
        requestAnimationFrame(draw);
    }

    resize();
    draw();

    window.addEventListener('resize', resize);
};

// ── Add future named animation types above this line ─────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// BG_BLOCKS dispatcher
// Handles the css_motion background option on v2-cards and v2-service-spotlight.
// Reads data-bg-type from the section and dispatches to BG_ANIMATIONS.
//
// Adding a new block that supports css_motion:
//   Add an entry with its section and canvas selectors.
// ─────────────────────────────────────────────────────────────────────────────

var BG_BLOCKS = [
    {
        section: '.v2-cards--bg-css_motion',
        canvas:  '.v2-cards__canvas'
    },
    {
        section: '.v2-service-spotlight--bg-css_motion',
        canvas:  '.v2-service-spotlight__canvas'
    },
    {
        section: '.v2-services-cards--bg-css_motion',
        canvas:  '.v2-services-cards__canvas'
    }
];

function initCssMotionBlocks() {
    if (BgUtils.prefersReducedMotion()) return;

    BG_BLOCKS.forEach(function (block) {
        document.querySelectorAll(block.section).forEach(function (section) {
            if (section.dataset.bgInit === 'true') return;
            section.dataset.bgInit = 'true';

            var type   = section.dataset.bgType || 'particle_network';
            var canvas = section.querySelector(block.canvas);
            if (!canvas) return;

            var animFn = BG_ANIMATIONS[type];
            if (!animFn) {
                console.warn('backgrounds.js: unknown animation type "' + type + '"');
                return;
            }
            animFn(canvas, section);
        });
    });
}

if (document.readyState === 'complete') {
    initCssMotionBlocks();
} else {
    window.addEventListener('load', initCssMotionBlocks);
}


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-neural-network
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify', nodes: 18 };
    var PALETTES = {
        agency: [[193,8,125],[60,137,201],[118,35,120]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[50,65,120],[70,80,140],[40,55,100]]
    };

    function NeuralNetwork(el) {
        BackgroundBlock.call(this, el, DEFAULTS);
        this.opts.nodes = parseInt(el.dataset.bgNodes || 18, 10);
        this.t = 0;
    }
    NeuralNetwork.prototype = Object.create(BackgroundBlock.prototype);
    NeuralNetwork.prototype.constructor = NeuralNetwork;

    NeuralNetwork.prototype.onResize = function () {};

    NeuralNetwork.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var count = this.simplified ? Math.floor(this.opts.nodes * 0.5) : this.opts.nodes;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#07070f';
        ctx.fillRect(0, 0, W, H);

        var nodes = [];
        for (var i = 0; i < count; i++) {
            nodes.push({
                x: W * (0.05 + 0.9 * (0.5 + 0.5 * Math.sin(i * 1.3 + t * spd * 0.18))),
                y: H * (0.05 + 0.9 * (0.5 + 0.5 * Math.cos(i * 1.7 + t * spd * 0.14)))
            });
        }

        var maxDist = Math.min(W, H) * 0.28;
        var accentCol = pal[0];

        for (var i = 0; i < nodes.length; i++) {
            for (var j = i + 1; j < nodes.length; j++) {
                var dx = nodes[i].x - nodes[j].x;
                var dy = nodes[i].y - nodes[j].y;
                var d  = Math.sqrt(dx * dx + dy * dy);
                if (d < maxDist) {
                    ctx.strokeStyle = 'rgba(' + accentCol[0] + ',' + accentCol[1] + ',' + accentCol[2] + ',' + (0.35 * (1 - d / maxDist)) + ')';
                    ctx.lineWidth = 0.65;
                    ctx.beginPath();
                    ctx.moveTo(nodes[i].x, nodes[i].y);
                    ctx.lineTo(nodes[j].x, nodes[j].y);
                    ctx.stroke();
                }
            }
        }

        nodes.forEach(function (n, i) {
            var c2 = pal[i % pal.length];
            ctx.beginPath();
            ctx.arc(n.x, n.y, 2.2, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(' + c2[0] + ',' + c2[1] + ',' + c2[2] + ',0.85)';
            ctx.fill();
        });

        this.t += 0.016;
    };

    function initV2BgNeuralNetwork() {
        document.querySelectorAll('.v2-bg-neural-network').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new NeuralNetwork(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgNeuralNetwork); }
    else { initV2BgNeuralNetwork(); }
    BG_ANIMATIONS['neural_network'] = function(canvas, section) { new NeuralNetwork(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-circuit-traces
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[128,200,192],[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[100,120,180],[70,80,140],[50,65,120]]
    };

    function CircuitTraces(el) {
        BackgroundBlock.call(this, el, DEFAULTS);
        this.t = 0;
        this.nodes = [];
    }
    CircuitTraces.prototype = Object.create(BackgroundBlock.prototype);
    CircuitTraces.prototype.constructor = CircuitTraces;

    CircuitTraces.prototype.buildNodes = function () {
        var W = this.w, H = this.h;
        var count = this.simplified ? 8 : 14;
        this.nodes = [];
        for (var i = 0; i < count; i++) {
            this.nodes.push({
                x: Math.round(W * (0.08 + 0.84 * ((i * 137.508) % 1)) / 24) * 24,
                y: Math.round(H * (0.08 + 0.84 * ((i * 97.32)  % 1)) / 24) * 24
            });
        }
    };

    CircuitTraces.prototype.onResize = function () { this.buildNodes(); };

    CircuitTraces.prototype.draw = function () {
        if (!this.nodes.length) this.buildNodes();
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var nodes = this.nodes;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#050a0f';
        ctx.fillRect(0, 0, W, H);

        for (var i = 0; i < nodes.length - 1; i++) {
            var a = nodes[i], b = nodes[i + 1];
            var progress = ((t * spd * 0.3 + i * 0.4) % 1);
            var hLen = Math.abs(b.x - a.x), vLen = Math.abs(b.y - a.y);
            var total = hLen + vLen;
            var drawn = total * Math.min(1, progress * 3);
            var col = pal[i % pal.length];
            var alpha = 0.18 + 0.12 * (Math.sin(t + i) * 0.5 + 0.5);

            ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')';
            ctx.lineWidth = 0.85;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            var hDrawn = Math.min(drawn, hLen);
            var ex = a.x + (b.x > a.x ? 1 : -1) * hDrawn;
            ctx.lineTo(ex, a.y);
            if (drawn > hLen) {
                ctx.lineTo(ex, a.y + (b.y > a.y ? 1 : -1) * Math.min(drawn - hLen, vLen));
            }
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(a.x, a.y, 2, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + (0.5 + 0.4 * (Math.sin(t * 2 + i) * 0.5 + 0.5)) + ')';
            ctx.fill();

            if (progress < 0.18) {
                var g = ctx.createRadialGradient(a.x, a.y, 0, a.x, a.y, 20);
                g.addColorStop(0, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.2)');
                g.addColorStop(1, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
                ctx.fillStyle = g;
                ctx.fillRect(a.x - 20, a.y - 20, 40, 40);
            }
        }
        this.t += 0.016;
    };

    function initV2BgCircuitTraces() {
        document.querySelectorAll('.v2-bg-circuit-traces').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new CircuitTraces(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgCircuitTraces); }
    else { initV2BgCircuitTraces(); }
    BG_ANIMATIONS['circuit_traces'] = function(canvas, section) { new CircuitTraces(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-wave-field
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125],[118,35,120]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[80,100,160],[60,80,140],[50,70,130]]
    };
    var LINE_COUNT = { low: 6, medium: 10, high: 16 };

    function WaveField(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    WaveField.prototype = Object.create(BackgroundBlock.prototype);
    WaveField.prototype.constructor = WaveField;

    WaveField.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var lineCount = this.simplified ? 5 : (LINE_COUNT[this.opts.intensity] || 10);

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#080810';
        ctx.fillRect(0, 0, W, H);

        for (var i = 0; i < lineCount; i++) {
            var col = pal[i % pal.length];
            var alpha = 0.06 + (i / lineCount) * 0.06;
            ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')';
            ctx.lineWidth = 1;
            ctx.beginPath();
            for (var x = 0; x <= W; x += 3) {
                var y = H / 2 + Math.sin(x * 0.018 + t * spd * 0.45 + i * 0.4) * (H * 0.12) * (1 - i / lineCount * 0.5) + (i - lineCount / 2) * (H / lineCount);
                if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
        this.t += 0.016;
    };

    function initV2BgWaveField() {
        document.querySelectorAll('.v2-bg-wave-field').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new WaveField(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgWaveField); }
    else { initV2BgWaveField(); }
    BG_ANIMATIONS['wave_field'] = function(canvas, section) { new WaveField(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-grid-distortion
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125],[118,35,120]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[80,100,160],[60,80,140],[50,70,130]]
    };
    var SPACING = { low: 28, medium: 20, high: 14 };

    function GridDistortion(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    GridDistortion.prototype = Object.create(BackgroundBlock.prototype);
    GridDistortion.prototype.constructor = GridDistortion;

    GridDistortion.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var sp = this.simplified ? 28 : (SPACING[this.opts.intensity] || 20);
        var col = pal[0];

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#070710';
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.11)';
        ctx.lineWidth = 0.7;

        for (var x = 0; x < W; x += sp) {
            ctx.beginPath();
            for (var y = 0; y <= H; y += 4) {
                var ox = Math.sin(y * 0.04 + t * spd * 0.55 + x * 0.02) * 3;
                ctx.lineTo(x + ox, y);
            }
            ctx.stroke();
        }
        for (var y = 0; y < H; y += sp) {
            ctx.beginPath();
            for (var x2 = 0; x2 <= W; x2 += 4) {
                var oy = Math.sin(x2 * 0.04 + t * spd * 0.45 + y * 0.02) * 3;
                ctx.lineTo(x2, y + oy);
            }
            ctx.stroke();
        }
        this.t += 0.016;
    };

    function initV2BgGridDistortion() {
        document.querySelectorAll('.v2-bg-grid-distortion').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new GridDistortion(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgGridDistortion); }
    else { initV2BgGridDistortion(); }
    BG_ANIMATIONS['grid_distortion'] = function(canvas, section) { new GridDistortion(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-aurora-glow
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var BAND_SETS = {
        agency: [{r:193,g:8,  b:125,y:0.28,amp:12,spd:0.22},{r:118,g:35, b:120,y:0.50,amp:10,spd:0.18},{r:60, g:137,b:201,y:0.70,amp:16,spd:0.26}],
        ocean:  [{r:6,  g:182,b:212,y:0.25,amp:14,spd:0.20},{r:56, g:189,b:248,y:0.50,amp:10,spd:0.17},{r:99, g:102,b:241,y:0.72,amp:14,spd:0.24}],
        ember:  [{r:239,g:68, b:68, y:0.30,amp:12,spd:0.22},{r:249,g:115,b:22, y:0.52,amp:10,spd:0.18},{r:236,g:72, b:153,y:0.70,amp:14,spd:0.26}],
        mono:   [{r:80, g:100,b:160,y:0.28,amp:12,spd:0.20},{r:60, g:80, b:140,y:0.52,amp:10,spd:0.16},{r:50, g:70, b:130,y:0.72,amp:14,spd:0.24}]
    };
    var OPACITY = { low: 0.12, medium: 0.22, high: 0.35 };

    function AuroraGlow(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    AuroraGlow.prototype = Object.create(BackgroundBlock.prototype);
    AuroraGlow.prototype.constructor = AuroraGlow;

    AuroraGlow.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var bands = BAND_SETS[this.opts.palette] || BAND_SETS.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var maxAlpha = OPACITY[this.opts.intensity] || 0.22;
        var bandSlice = this.simplified ? bands.slice(0, 2) : bands;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060810';
        ctx.fillRect(0, 0, W, H);

        bandSlice.forEach(function (b, i) {
            var cy = H * (b.y + Math.sin(t * b.spd * spd + i) * 0.07);
            var halfH = H * 0.18 + b.amp;
            var grad = ctx.createLinearGradient(0, cy - halfH, 0, cy + halfH);
            grad.addColorStop(0,   'rgba(' + b.r + ',' + b.g + ',' + b.b + ',0)');
            grad.addColorStop(0.5, 'rgba(' + b.r + ',' + b.g + ',' + b.b + ',' + maxAlpha + ')');
            grad.addColorStop(1,   'rgba(' + b.r + ',' + b.g + ',' + b.b + ',0)');
            ctx.beginPath();
            ctx.moveTo(0, cy - halfH);
            for (var x = 0; x <= W; x += 4) {
                ctx.lineTo(x, cy + Math.sin(x * 0.018 + t * 0.3 * spd + i * 1.4) * b.amp);
            }
            ctx.lineTo(W, cy + halfH);
            ctx.lineTo(0, cy + halfH);
            ctx.closePath();
            ctx.fillStyle = grad;
            ctx.fill();
        });
        this.t += 0.016;
    };

    function initV2BgAuroraGlow() {
        document.querySelectorAll('.v2-bg-aurora-glow').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new AuroraGlow(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgAuroraGlow); }
    else { initV2BgAuroraGlow(); }
    BG_ANIMATIONS['aurora_glow'] = function(canvas, section) { new AuroraGlow(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-particles
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[128,200,192],[193,8,125],[60,137,201]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[100,120,170],[80,100,150],[60,80,130]]
    };
    var COUNT = { low: 15, medium: 28, high: 45 };

    function Particles(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; this.pts = []; }
    Particles.prototype = Object.create(BackgroundBlock.prototype);
    Particles.prototype.constructor = Particles;

    Particles.prototype.buildParticles = function () {
        var count = this.simplified ? 12 : (COUNT[this.opts.intensity] || 28);
        this.pts = [];
        for (var i = 0; i < count; i++) {
            this.pts.push({
                sx: Math.random(), sy: Math.random(),
                vx: (Math.random() - 0.5) * 0.004,
                vy: (Math.random() - 0.5) * 0.003,
                r: 0.8 + Math.random() * 1.6,
                phase: Math.random() * Math.PI * 2,
                ci: i % 3
            });
        }
    };

    Particles.prototype.onResize = function () { this.buildParticles(); };

    Particles.prototype.draw = function () {
        if (!this.pts.length) this.buildParticles();
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#070710';
        ctx.fillRect(0, 0, W, H);

        this.pts.forEach(function (p) {
            p.sx = (p.sx + p.vx * spd + 1) % 1;
            p.sy = (p.sy + p.vy * spd + 1) % 1;
            var col = pal[p.ci];
            var alpha = 0.25 + Math.sin(t * 1.2 + p.phase) * 0.15;
            ctx.beginPath();
            ctx.arc(p.sx * W, p.sy * H, p.r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')';
            ctx.fill();
        });
        this.t += 0.016;
    };

    function initV2BgParticles() {
        document.querySelectorAll('.v2-bg-particles').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new Particles(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgParticles); }
    else { initV2BgParticles(); }
    BG_ANIMATIONS['particles'] = function(canvas, section) { new Particles(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-starfield
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var COUNT = { low: 30, medium: 60, high: 100 };

    function Starfield(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; this.stars = []; }
    Starfield.prototype = Object.create(BackgroundBlock.prototype);
    Starfield.prototype.constructor = Starfield;

    Starfield.prototype.buildStars = function () {
        var count = this.simplified ? 30 : (COUNT[this.opts.intensity] || 60);
        this.stars = [];
        for (var i = 0; i < count; i++) {
            var layer = Math.floor(Math.random() * 4);
            this.stars.push({
                x: Math.random(), y: Math.random(),
                r: 0.3 + layer * 0.3,
                speed: 0.3 + layer * 0.4,
                alpha: 0.15 + layer * 0.18,
                twinklePhase: Math.random() * Math.PI * 2
            });
        }
    };

    Starfield.prototype.onResize = function () { this.buildStars(); };

    Starfield.prototype.draw = function () {
        if (!this.stars.length) this.buildStars();
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#040408';
        ctx.fillRect(0, 0, W, H);

        this.stars.forEach(function (s) {
            s.x = (s.x + s.speed * spd * 0.0004 + 1) % 1;
            var twinkle = 0.8 + Math.sin(t * 1.4 + s.twinklePhase) * 0.2;
            ctx.beginPath();
            ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255,255,255,' + (s.alpha * twinkle) + ')';
            ctx.fill();
        });
        this.t += 0.016;
    };

    function initV2BgStarfield() {
        document.querySelectorAll('.v2-bg-starfield').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new Starfield(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgStarfield); }
    else { initV2BgStarfield(); }
    BG_ANIMATIONS['starfield'] = function(canvas, section) { new Starfield(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-grain-drift
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'disable' };
    var PALETTES = {
        agency: [[118,35,120],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var THRESHOLD = { low: 80, medium: 120, high: 170 };

    function GrainDrift(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    GrainDrift.prototype = Object.create(BackgroundBlock.prototype);
    GrainDrift.prototype.constructor = GrainDrift;

    GrainDrift.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var thresh = THRESHOLD[this.opts.intensity] || 120;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;

        var grad = ctx.createLinearGradient(0, 0, W, H);
        grad.addColorStop(0, '#120e1c');
        grad.addColorStop(1, '#0a0f20');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);

        var id = ctx.createImageData(W, H);
        var d  = id.data;
        var seed = Math.floor(t * spd * 20);
        for (var i = 0; i < d.length; i += 4) {
            var px = (i / 4) % W;
            var py = Math.floor((i / 4) / W);
            var hash = ((px * 374761393 + py * 668265263 + seed * 2246822519) >>> 0) % 1000;
            var v = hash < thresh ? Math.floor(hash / 3.5) : 0;
            d[i] = d[i+1] = d[i+2] = v;
            d[i+3] = v > 0 ? 65 : 0;
        }
        ctx.putImageData(id, 0, 0);

        pal.forEach(function (col, ci) {
            var glow = ctx.createRadialGradient(W * (0.3 + ci * 0.4), H * (0.3 + ci * 0.2), 0, W * (0.3 + ci * 0.4), H * (0.3 + ci * 0.2), W * 0.5);
            glow.addColorStop(0, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.18)');
            glow.addColorStop(1, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, W, H);
        });

        this.t += 0.016;
    };

    function initV2BgGrainDrift() {
        document.querySelectorAll('.v2-bg-grain-drift').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new GrainDrift(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgGrainDrift); }
    else { initV2BgGrainDrift(); }
    BG_ANIMATIONS['grain_drift'] = function(canvas, section) { new GrainDrift(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-glass-orbs
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var ORB_SETS = {
        agency: [{r:193,g:8,  b:125,cx:0.22,cy:0.45,rad:0.38},{r:60, g:137,b:201,cx:0.72,cy:0.28,rad:0.42},{r:118,g:35,b:120,cx:0.50,cy:0.75,rad:0.32}],
        ocean:  [{r:6,  g:182,b:212,cx:0.25,cy:0.40,rad:0.40},{r:56, g:189,b:248,cx:0.70,cy:0.30,rad:0.38},{r:99, g:102,b:241,cx:0.50,cy:0.72,rad:0.30}],
        ember:  [{r:239,g:68, b:68, cx:0.20,cy:0.45,rad:0.38},{r:249,g:115,b:22, cx:0.75,cy:0.28,rad:0.42},{r:236,g:72, b:153,cx:0.50,cy:0.75,rad:0.32}],
        mono:   [{r:80, g:100,b:160,cx:0.22,cy:0.45,rad:0.38},{r:60, g:80, b:140,cx:0.72,cy:0.28,rad:0.42},{r:50, g:70, b:130,cx:0.50,cy:0.75,rad:0.32}]
    };
    var ALPHA = { low: 0.14, medium: 0.24, high: 0.36 };

    function GlassOrbs(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    GlassOrbs.prototype = Object.create(BackgroundBlock.prototype);
    GlassOrbs.prototype.constructor = GlassOrbs;

    GlassOrbs.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var orbs = ORB_SETS[this.opts.palette] || ORB_SETS.agency;
        var spd  = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var alpha = ALPHA[this.opts.intensity] || 0.24;
        var orbList = this.simplified ? orbs.slice(0, 2) : orbs;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#080810';
        ctx.fillRect(0, 0, W, H);

        orbList.forEach(function (o, i) {
            var cx = W * (o.cx + Math.sin(t * spd * 0.18 + i * 2.1) * 0.1);
            var cy = H * (o.cy + Math.cos(t * spd * 0.15 + i * 1.7) * 0.09);
            var rad = Math.min(W, H) * o.rad;
            var gr  = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
            gr.addColorStop(0,    'rgba(' + o.r + ',' + o.g + ',' + o.b + ',' + alpha + ')');
            gr.addColorStop(0.55, 'rgba(' + o.r + ',' + o.g + ',' + o.b + ',' + (alpha * 0.33) + ')');
            gr.addColorStop(1,    'rgba(' + o.r + ',' + o.g + ',' + o.b + ',0)');
            ctx.fillStyle = gr;
            ctx.fillRect(0, 0, W, H);
        });
        this.t += 0.016;
    };

    function initV2BgGlassOrbs() {
        document.querySelectorAll('.v2-bg-glass-orbs').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new GlassOrbs(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgGlassOrbs); }
    else { initV2BgGlassOrbs(); }
    BG_ANIMATIONS['glass_orbs'] = function(canvas, section) { new GlassOrbs(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-light-rays
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var BEAM_COUNT = { low: 3, medium: 5, high: 7 };
    var ALPHA = { low: 0.06, medium: 0.10, high: 0.16 };

    function LightRays(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    LightRays.prototype = Object.create(BackgroundBlock.prototype);
    LightRays.prototype.constructor = LightRays;

    LightRays.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var count = this.simplified ? 3 : (BEAM_COUNT[this.opts.intensity] || 5);
        var maxAlpha = ALPHA[this.opts.intensity] || 0.10;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060610';
        ctx.fillRect(0, 0, W, H);

        for (var i = 0; i < count; i++) {
            var rx = W * (0.08 + i * (0.84 / count) + Math.sin(t * spd * 0.18 + i) * 0.04);
            var col = pal[i % pal.length];
            var angle = Math.PI / 3 + i * 0.2;
            var gr = ctx.createLinearGradient(rx, 0, rx + Math.cos(angle) * 220, H);
            gr.addColorStop(0,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            gr.addColorStop(0.3, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + maxAlpha + ')');
            gr.addColorStop(0.7, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + (maxAlpha * 0.5) + ')');
            gr.addColorStop(1,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.fillStyle = gr;
            ctx.fillRect(0, 0, W, H);
        }
        this.t += 0.016;
    };

    function initV2BgLightRays() {
        document.querySelectorAll('.v2-bg-light-rays').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new LightRays(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgLightRays); }
    else { initV2BgLightRays(); }
    BG_ANIMATIONS['light_rays'] = function(canvas, section) { new LightRays(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-blob-morph
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var BLOB_SETS = {
        agency: [{r:193,g:8,  b:125,cx:0.30,cy:0.50},{r:60, g:137,b:201,cx:0.68,cy:0.35},{r:118,g:35,b:120,cx:0.55,cy:0.72}],
        ocean:  [{r:6,  g:182,b:212,cx:0.28,cy:0.48},{r:56, g:189,b:248,cx:0.70,cy:0.32},{r:99, g:102,b:241,cx:0.52,cy:0.70}],
        ember:  [{r:239,g:68, b:68, cx:0.28,cy:0.50},{r:249,g:115,b:22, cx:0.68,cy:0.35},{r:236,g:72, b:153,cx:0.55,cy:0.72}],
        mono:   [{r:80, g:100,b:160,cx:0.30,cy:0.50},{r:60, g:80, b:140,cx:0.68,cy:0.35},{r:50, g:70, b:130,cx:0.55,cy:0.72}]
    };
    var BASE_R = { low: 0.22, medium: 0.30, high: 0.40 };

    function BlobMorph(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    BlobMorph.prototype = Object.create(BackgroundBlock.prototype);
    BlobMorph.prototype.constructor = BlobMorph;

    BlobMorph.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var blobs = BLOB_SETS[this.opts.palette] || BLOB_SETS.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var baseR = BASE_R[this.opts.intensity] || 0.30;
        var blobList = this.simplified ? blobs.slice(0, 2) : blobs;
        var pts = 8;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#090810';
        ctx.fillRect(0, 0, W, H);

        blobList.forEach(function (b, bi) {
            var cx = W * (b.cx + Math.sin(t * spd * 0.28 + bi * 2.1) * 0.08);
            var cy = H * (b.cy + Math.cos(t * spd * 0.22 + bi * 1.7) * 0.08);
            var r  = Math.min(W, H) * baseR;
            var gr = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.4);
            gr.addColorStop(0,   'rgba(' + b.r + ',' + b.g + ',' + b.b + ',0.28)');
            gr.addColorStop(0.6, 'rgba(' + b.r + ',' + b.g + ',' + b.b + ',0.08)');
            gr.addColorStop(1,   'rgba(' + b.r + ',' + b.g + ',' + b.b + ',0)');
            ctx.beginPath();
            for (var i = 0; i <= pts; i++) {
                var angle = (i / pts) * Math.PI * 2;
                var morph = r * (1 + 0.22 * Math.sin(t * spd + i * 1.3));
                var px = cx + Math.cos(angle) * morph;
                var py = cy + Math.sin(angle) * morph * 0.8;
                if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.fillStyle = gr;
            ctx.fill();
        });
        this.t += 0.016;
    };

    function initV2BgBlobMorph() {
        document.querySelectorAll('.v2-bg-blob-morph').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new BlobMorph(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgBlobMorph); }
    else { initV2BgBlobMorph(); }
    BG_ANIMATIONS['blob_morph'] = function(canvas, section) { new BlobMorph(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-liquid-distortion
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'disable' };
    var PALETTES = {
        agency: [[128,200,192],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[100,120,170],[80,100,150]]
    };
    var RES = { low: 20, medium: 14, high: 10 };

    function LiquidDistortion(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    LiquidDistortion.prototype = Object.create(BackgroundBlock.prototype);
    LiquidDistortion.prototype.constructor = LiquidDistortion;

    LiquidDistortion.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var res = RES[this.opts.intensity] || 14;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#050510';
        ctx.fillRect(0, 0, W, H);

        for (var gy = 0; gy <= H; gy += res) {
            for (var gx = 0; gx <= W; gx += res) {
                var dx = Math.sin(gx * 0.038 + t * spd * 0.45) * 5.5;
                var dy = Math.cos(gy * 0.038 + t * spd * 0.35) * 5.5;
                var wave = Math.sin(gx * 0.02 + gy * 0.02 + t * spd) * 0.5 + 0.5;
                var col  = pal[Math.floor(wave * pal.length) % pal.length];
                var alpha = 0.03 + wave * 0.06;
                ctx.fillStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')';
                ctx.fillRect(gx + dx, gy + dy, 1.5, 1.5);
            }
        }
        this.t += 0.016;
    };

    function initV2BgLiquidDistortion() {
        document.querySelectorAll('.v2-bg-liquid-distortion').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new LiquidDistortion(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgLiquidDistortion); }
    else { initV2BgLiquidDistortion(); }
    BG_ANIMATIONS['liquid_distortion'] = function(canvas, section) { new LiquidDistortion(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-dunes-hills
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var LAYER_SETS = {
        agency: [{r:60, g:137,b:201,yFrac:0.60,amp:14,spd:0.28,alpha:0.14},{r:118,g:35, b:120,yFrac:0.72,amp:11,spd:0.44,alpha:0.18},{r:193,g:8,  b:125,yFrac:0.84,amp:9,  spd:0.62,alpha:0.13}],
        ocean:  [{r:6,  g:182,b:212,yFrac:0.60,amp:14,spd:0.28,alpha:0.14},{r:56, g:189,b:248,yFrac:0.72,amp:11,spd:0.44,alpha:0.18},{r:99, g:102,b:241,yFrac:0.84,amp:9,  spd:0.62,alpha:0.13}],
        ember:  [{r:239,g:68, b:68, yFrac:0.60,amp:14,spd:0.28,alpha:0.14},{r:249,g:115,b:22, yFrac:0.72,amp:11,spd:0.44,alpha:0.18},{r:236,g:72, b:153,yFrac:0.84,amp:9,  spd:0.62,alpha:0.13}],
        mono:   [{r:80, g:100,b:160,yFrac:0.60,amp:14,spd:0.28,alpha:0.14},{r:60, g:80, b:140,yFrac:0.72,amp:11,spd:0.44,alpha:0.18},{r:50, g:70, b:130,yFrac:0.84,amp:9,  spd:0.62,alpha:0.13}]
    };
    var AMP_SCALE = { low: 0.6, medium: 1.0, high: 1.5 };

    function DunesHills(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    DunesHills.prototype = Object.create(BackgroundBlock.prototype);
    DunesHills.prototype.constructor = DunesHills;

    DunesHills.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var layers = LAYER_SETS[this.opts.palette] || LAYER_SETS.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var ampScale = AMP_SCALE[this.opts.intensity] || 1.0;
        var layerList = this.simplified ? layers.slice(1) : layers;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#07080f';
        ctx.fillRect(0, 0, W, H);

        layerList.forEach(function (l, i) {
            ctx.beginPath();
            ctx.moveTo(0, H);
            for (var x = 0; x <= W; x += 4) {
                var y = H * l.yFrac + Math.sin(x * 0.018 + t * l.spd * spd + i) * l.amp * ampScale;
                ctx.lineTo(x, y);
            }
            ctx.lineTo(W, H);
            ctx.closePath();
            ctx.fillStyle = 'rgba(' + l.r + ',' + l.g + ',' + l.b + ',' + l.alpha + ')';
            ctx.fill();
        });
        this.t += 0.016;
    };

    function initV2BgDunesHills() {
        document.querySelectorAll('.v2-bg-dunes-hills').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new DunesHills(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgDunesHills); }
    else { initV2BgDunesHills(); }
    BG_ANIMATIONS['dunes_hills'] = function(canvas, section) { new DunesHills(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-scanlines-shimmer
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'disable' };
    var PALETTES = {
        agency: [[128,200,192],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[100,120,170],[80,100,150]]
    };
    var LINE_GAP = { low: 5, medium: 3, high: 2 };
    var SHIMMER_ALPHA = { low: 0.04, medium: 0.07, high: 0.12 };

    function ScanlinesShimmer(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    ScanlinesShimmer.prototype = Object.create(BackgroundBlock.prototype);
    ScanlinesShimmer.prototype.constructor = ScanlinesShimmer;

    ScanlinesShimmer.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var gap = LINE_GAP[this.opts.intensity] || 3;
        var shimAlpha = SHIMMER_ALPHA[this.opts.intensity] || 0.07;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060608';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = 'rgba(255,255,255,0.038)';
        ctx.lineWidth = 1;
        for (var y = 0; y < H; y += gap) {
            ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
        }

        var sx = ((t * spd * 55) % (W + 100)) - 50;
        var col = pal[0];
        var sg = ctx.createLinearGradient(sx - 50, 0, sx + 50, 0);
        sg.addColorStop(0,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
        sg.addColorStop(0.5, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + shimAlpha + ')');
        sg.addColorStop(1,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
        ctx.fillStyle = sg;
        ctx.fillRect(0, 0, W, H);

        this.t += 0.016;
    };

    function initV2BgScanlinesShimmer() {
        document.querySelectorAll('.v2-bg-scanlines-shimmer').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new ScanlinesShimmer(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgScanlinesShimmer); }
    else { initV2BgScanlinesShimmer(); }
    BG_ANIMATIONS['scanlines_shimmer'] = function(canvas, section) { new ScanlinesShimmer(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-radar-sweep
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[128,200,192],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[100,130,180],[80,110,160]]
    };

    function RadarSweep(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    RadarSweep.prototype = Object.create(BackgroundBlock.prototype);
    RadarSweep.prototype.constructor = RadarSweep;

    RadarSweep.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var col = pal[0];
        var cx = W / 2, cy = H / 2;
        var maxR = Math.min(W, H) * 0.44;
        var rings = this.simplified ? 3 : 5;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#040a0a';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.07)';
        ctx.lineWidth = 0.6;
        for (var r = 1; r <= rings; r++) {
            ctx.beginPath();
            ctx.arc(cx, cy, maxR * r / rings, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.beginPath(); ctx.moveTo(cx - maxR, cy); ctx.lineTo(cx + maxR, cy); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx, cy - maxR); ctx.lineTo(cx, cy + maxR); ctx.stroke();

        var angle = (t * spd * 0.7) % (Math.PI * 2);
        var sweep = Math.PI * 0.5;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, maxR, angle - sweep, angle);
        ctx.closePath();
        var sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR);
        sg.addColorStop(0,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.0)');
        sg.addColorStop(0.7, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.04)');
        sg.addColorStop(1,   'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.12)');
        ctx.fillStyle = sg;
        ctx.fill();
        ctx.restore();

        ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.55)';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
        ctx.stroke();

        var pingCount = this.simplified ? 2 : 4;
        for (var p = 0; p < pingCount; p++) {
            var pa = angle - (p + 1) * 0.2;
            var pr = maxR * (0.25 + p * 0.18);
            var palpha = Math.max(0, (1 - p * 0.28) * 0.5);
            ctx.beginPath();
            ctx.arc(cx + Math.cos(pa) * pr, cy + Math.sin(pa) * pr, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + palpha + ')';
            ctx.fill();
        }

        ctx.beginPath(); ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.7)';
        ctx.fill();

        this.t += 0.016;
    };

    function initV2BgRadarSweep() {
        document.querySelectorAll('.v2-bg-radar-sweep').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new RadarSweep(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgRadarSweep); }
    else { initV2BgRadarSweep(); }
    BG_ANIMATIONS['radar_sweep'] = function(canvas, section) { new RadarSweep(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-hex-grid-pulse
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var HEX_SIZE = { low: 30, medium: 22, high: 16 };

    function HexGridPulse(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    HexGridPulse.prototype = Object.create(BackgroundBlock.prototype);
    HexGridPulse.prototype.constructor = HexGridPulse;

    HexGridPulse.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var s = this.simplified ? 30 : (HEX_SIZE[this.opts.intensity] || 22);
        var base = pal[0], accent = pal[1];

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060810';
        ctx.fillRect(0, 0, W, H);

        var rows = Math.ceil(H / (s * 1.5)) + 2;
        var cols = Math.ceil(W / (s * 1.73)) + 2;

        for (var row = 0; row < rows; row++) {
            for (var col = 0; col < cols; col++) {
                var hx = col * s * 1.73 + (row % 2) * s * 0.866;
                var hy = row * s * 1.5;
                var dist = Math.sqrt(Math.pow(hx - W / 2, 2) + Math.pow(hy - H / 2, 2));
                var wave = Math.sin(dist * 0.04 - t * spd * 1.6) * 0.5 + 0.5;

                ctx.beginPath();
                for (var i = 0; i < 6; i++) {
                    var a = Math.PI / 3 * i - Math.PI / 6;
                    var px = hx + s * 0.48 * Math.cos(a);
                    var py = hy + s * 0.48 * Math.sin(a);
                    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
                }
                ctx.closePath();

                ctx.fillStyle = 'rgba(' + base[0] + ',' + base[1] + ',' + base[2] + ',' + (0.04 + wave * 0.10) + ')';
                ctx.fill();
                ctx.strokeStyle = 'rgba(' + base[0] + ',' + base[1] + ',' + base[2] + ',' + (0.06 + wave * 0.12) + ')';
                ctx.lineWidth = 0.7;
                ctx.stroke();

                if (wave > 0.85) {
                    ctx.beginPath();
                    for (var i2 = 0; i2 < 6; i2++) {
                        var a2 = Math.PI / 3 * i2 - Math.PI / 6;
                        var px2 = hx + s * 0.48 * Math.cos(a2);
                        var py2 = hy + s * 0.48 * Math.sin(a2);
                        if (i2 === 0) ctx.moveTo(px2, py2); else ctx.lineTo(px2, py2);
                    }
                    ctx.closePath();
                    ctx.strokeStyle = 'rgba(' + accent[0] + ',' + accent[1] + ',' + accent[2] + ',0.35)';
                    ctx.lineWidth = 0.9;
                    ctx.stroke();
                }
            }
        }
        this.t += 0.016;
    };

    function initV2BgHexGridPulse() {
        document.querySelectorAll('.v2-bg-hex-grid-pulse').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new HexGridPulse(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgHexGridPulse); }
    else { initV2BgHexGridPulse(); }
    BG_ANIMATIONS['hex_grid_pulse'] = function(canvas, section) { new HexGridPulse(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-topo-lines
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[128,200,192],[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241]],
        ember:  [[239,68,68],[249,115,22],[236,72,153]],
        mono:   [[100,120,170],[80,100,150],[60,80,130]]
    };
    var LINE_COUNT = { low: 5, medium: 9, high: 14 };

    function TopoLines(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    TopoLines.prototype = Object.create(BackgroundBlock.prototype);
    TopoLines.prototype.constructor = TopoLines;

    TopoLines.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var count = this.simplified ? 5 : (LINE_COUNT[this.opts.intensity] || 9);

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#070710';
        ctx.fillRect(0, 0, W, H);

        for (var ln = 0; ln < count; ln++) {
            var col = pal[ln % pal.length];
            ctx.strokeStyle = 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.11)';
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            for (var x = 0; x <= W; x += 4) {
                var y = H / 2 + Math.sin(x * 0.02 + t * spd * 0.28 + ln * 0.8) * 22 + (ln - count / 2) * (H / count * 0.8);
                if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
        this.t += 0.016;
    };

    function initV2BgTopoLines() {
        document.querySelectorAll('.v2-bg-topo-lines').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new TopoLines(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgTopoLines); }
    else { initV2BgTopoLines(); }
    BG_ANIMATIONS['topo_lines'] = function(canvas, section) { new TopoLines(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-ribbon-trails
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[193,8,125],[60,137,201],[118,35,120],[128,200,192]],
        ocean:  [[6,182,212],[56,189,248],[99,102,241],[6,200,180]],
        ember:  [[239,68,68],[249,115,22],[236,72,153],[220,100,50]],
        mono:   [[80,100,160],[60,80,140],[100,120,180],[70,90,150]]
    };
    var RIBBON_COUNT = { low: 3, medium: 5, high: 7 };

    function RibbonTrails(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    RibbonTrails.prototype = Object.create(BackgroundBlock.prototype);
    RibbonTrails.prototype.constructor = RibbonTrails;

    RibbonTrails.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var count = this.simplified ? 3 : (RIBBON_COUNT[this.opts.intensity] || 5);

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060610';
        ctx.fillRect(0, 0, W, H);

        for (var r = 0; r < count; r++) {
            var col = pal[r % pal.length];
            var yOff = H * (0.12 + r * (0.76 / count));
            var phase = r * 1.4;

            ctx.beginPath();
            for (var x = 0; x <= W; x += 3) {
                var y = yOff + Math.sin(x * 0.016 + t * spd * 0.38 + phase) * H * 0.08;
                if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            var gr = ctx.createLinearGradient(0, 0, W, 0);
            gr.addColorStop(0,          'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            gr.addColorStop(0.25 + r * 0.04, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.18)');
            gr.addColorStop(0.70,        'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0.13)');
            gr.addColorStop(1,          'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.strokeStyle = gr;
            ctx.lineWidth = 1.4;
            ctx.stroke();
        }
        this.t += 0.016;
    };

    function initV2BgRibbonTrails() {
        document.querySelectorAll('.v2-bg-ribbon-trails').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new RibbonTrails(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgRibbonTrails); }
    else { initV2BgRibbonTrails(); }
    BG_ANIMATIONS['ribbon_trails'] = function(canvas, section) { new RibbonTrails(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-reactive-distortion
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'disable' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var SPACING = { low: 26, medium: 20, high: 14 };

    function ReactiveDistortion(el) {
        BackgroundBlock.call(this, el, DEFAULTS);
        this.t = 0;
        this.mx = 0.5; this.my = 0.5;
        this._mouseHandler = null;
    }
    ReactiveDistortion.prototype = Object.create(BackgroundBlock.prototype);
    ReactiveDistortion.prototype.constructor = ReactiveDistortion;

    ReactiveDistortion.prototype.init = function () {
        BackgroundBlock.prototype.init.call(this);
        var self = this;
        this._mouseHandler = function (e) {
            var rect = self.el.getBoundingClientRect();
            self.mx = (e.clientX - rect.left) / rect.width;
            self.my = (e.clientY - rect.top)  / rect.height;
        };
        this.el.addEventListener('mousemove', this._mouseHandler, { passive: true });
    };

    ReactiveDistortion.prototype.destroy = function () {
        BackgroundBlock.prototype.destroy.call(this);
        if (this._mouseHandler) this.el.removeEventListener('mousemove', this._mouseHandler);
    };

    ReactiveDistortion.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var sp = SPACING[this.opts.intensity] || 20;
        var base = pal[0], accent = pal[1];

        var fmx = this.mx * W * 0.6 + W * (0.2 + Math.sin(t * spd * 0.4) * 0.3);
        var fmy = this.my * H * 0.6 + H * (0.2 + Math.cos(t * spd * 0.32) * 0.28);

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060612';
        ctx.fillRect(0, 0, W, H);

        for (var gy = 0; gy < H; gy += sp) {
            for (var gx = 0; gx < W; gx += sp) {
                var dx = gx - fmx, dy = gy - fmy;
                var dist = Math.sqrt(dx * dx + dy * dy) || 1;
                var strength = Math.max(0, 1 - dist / 130);
                if (strength < 0.04) continue;
                var wave = Math.sin(dist * 0.06 - t * spd * 2.0) * strength;
                var ex = gx + dx / dist * wave * 12;
                var ey = gy + dy / dist * wave * 12;
                ctx.strokeStyle = 'rgba(' + base[0] + ',' + base[1] + ',' + base[2] + ',' + (0.08 + strength * 0.18) + ')';
                ctx.lineWidth = 0.6;
                ctx.beginPath(); ctx.moveTo(gx, gy); ctx.lineTo(ex, ey); ctx.stroke();
                if (strength > 0.55) {
                    ctx.beginPath(); ctx.arc(gx, gy, 0.9, 0, Math.PI * 2);
                    ctx.fillStyle = 'rgba(' + accent[0] + ',' + accent[1] + ',' + accent[2] + ',' + (strength * 0.5) + ')';
                    ctx.fill();
                }
            }
        }
        this.t += 0.016;
    };

    function initV2BgReactiveDistortion() {
        document.querySelectorAll('.v2-bg-reactive-distortion').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new ReactiveDistortion(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgReactiveDistortion); }
    else { initV2BgReactiveDistortion(); }
    BG_ANIMATIONS['reactive_distortion'] = function(canvas, section) { new ReactiveDistortion(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-morphing-polygon
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var SHAPE_SETS = {
        agency: [{pts:6,r:0.38,spd:0.22,phase:0,    col:[60, 137,201],alpha:0.18},{pts:5,r:0.28,spd:-0.16,phase:1.2,col:[193,8,  125],alpha:0.15},{pts:8,r:0.20,spd:0.30,phase:2.4,col:[128,200,192],alpha:0.20}],
        ocean:  [{pts:6,r:0.38,spd:0.22,phase:0,    col:[6,  182,212],alpha:0.18},{pts:5,r:0.28,spd:-0.16,phase:1.2,col:[56, 189,248],alpha:0.15},{pts:8,r:0.20,spd:0.30,phase:2.4,col:[99, 102,241],alpha:0.20}],
        ember:  [{pts:6,r:0.38,spd:0.22,phase:0,    col:[239,68, 68], alpha:0.18},{pts:5,r:0.28,spd:-0.16,phase:1.2,col:[249,115,22], alpha:0.15},{pts:8,r:0.20,spd:0.30,phase:2.4,col:[236,72, 153],alpha:0.20}],
        mono:   [{pts:6,r:0.38,spd:0.22,phase:0,    col:[80, 100,160],alpha:0.18},{pts:5,r:0.28,spd:-0.16,phase:1.2,col:[60, 80, 140],alpha:0.15},{pts:8,r:0.20,spd:0.30,phase:2.4,col:[100,120,180],alpha:0.20}]
    };
    var R_SCALE = { low: 0.65, medium: 1.0, high: 1.35 };

    function MorphingPolygon(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    MorphingPolygon.prototype = Object.create(BackgroundBlock.prototype);
    MorphingPolygon.prototype.constructor = MorphingPolygon;

    MorphingPolygon.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var shapes = SHAPE_SETS[this.opts.palette] || SHAPE_SETS.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var rScale = R_SCALE[this.opts.intensity] || 1.0;
        var cx = W / 2, cy = H / 2;
        var shapeList = this.simplified ? shapes.slice(0, 2) : shapes;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#050510';
        ctx.fillRect(0, 0, W, H);

        shapeList.forEach(function (sh) {
            var verts = [];
            for (var i = 0; i < sh.pts; i++) {
                var base  = (i / sh.pts) * Math.PI * 2 + t * sh.spd * spd + sh.phase;
                var morph = 1 + 0.18 * Math.sin(t * spd * 0.9 + i * 2.1);
                var r     = Math.min(W, H) * sh.r * rScale * morph;
                verts.push({ x: cx + Math.cos(base) * r, y: cy + Math.sin(base) * r * 0.65 });
            }

            ctx.beginPath();
            verts.forEach(function (v, i) { if (i === 0) ctx.moveTo(v.x, v.y); else ctx.lineTo(v.x, v.y); });
            ctx.closePath();
            ctx.strokeStyle = 'rgba(' + sh.col[0] + ',' + sh.col[1] + ',' + sh.col[2] + ',' + sh.alpha + ')';
            ctx.lineWidth = 0.8;
            ctx.stroke();

            for (var i = 0; i < verts.length; i += 2) {
                var j = (i + 2) % verts.length;
                ctx.strokeStyle = 'rgba(' + sh.col[0] + ',' + sh.col[1] + ',' + sh.col[2] + ',' + (sh.alpha * 0.45) + ')';
                ctx.lineWidth = 0.5;
                ctx.beginPath(); ctx.moveTo(verts[i].x, verts[i].y); ctx.lineTo(verts[j].x, verts[j].y); ctx.stroke();
            }

            verts.forEach(function (v) {
                ctx.beginPath(); ctx.arc(v.x, v.y, 1.4, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(' + sh.col[0] + ',' + sh.col[1] + ',' + sh.col[2] + ',0.6)';
                ctx.fill();
            });
        });
        this.t += 0.016;
    };

    function initV2BgMorphingPolygon() {
        document.querySelectorAll('.v2-bg-morphing-polygon').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new MorphingPolygon(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgMorphingPolygon); }
    else { initV2BgMorphingPolygon(); }
    BG_ANIMATIONS['morphing_polygon'] = function(canvas, section) { new MorphingPolygon(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-atmospheric-fog
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[40,53,131],[118,35,120],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248],[6,100,180]],
        ember:  [[239,68,68],[249,115,22],[200,40,100]],
        mono:   [[60,80,140],[80,100,160],[50,70,130]]
    };
    var ALPHA = { low: 0.07, medium: 0.12, high: 0.18 };

    function AtmosphericFog(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    AtmosphericFog.prototype = Object.create(BackgroundBlock.prototype);
    AtmosphericFog.prototype.constructor = AtmosphericFog;

    AtmosphericFog.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var alpha = ALPHA[this.opts.intensity] || 0.12;
        var count = this.simplified ? 2 : 4;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#080810';
        ctx.fillRect(0, 0, W, H);

        for (var i = 0; i < count; i++) {
            var col = pal[i % pal.length];
            var fx = ((t * spd * 10 * (i + 1) + i * 200) % (W * 1.8)) - W * 0.4;
            var fy = H * (0.3 + i * 0.18 + Math.sin(t * spd * 0.15 + i) * 0.1);
            var gr = ctx.createRadialGradient(fx, fy, 0, fx, fy, W * 0.5);
            gr.addColorStop(0, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')');
            gr.addColorStop(1, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)');
            ctx.fillStyle = gr;
            ctx.fillRect(0, 0, W, H);
        }
        this.t += 0.016;
    };

    function initV2BgAtmosphericFog() {
        document.querySelectorAll('.v2-bg-atmospheric-fog').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new AtmosphericFog(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgAtmosphericFog); }
    else { initV2BgAtmosphericFog(); }
    BG_ANIMATIONS['atmospheric_fog'] = function(canvas, section) { new AtmosphericFog(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-pulse-grid
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[193,8,125]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var SPACING = { low: 22, medium: 16, high: 11 };

    function PulseGrid(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    PulseGrid.prototype = Object.create(BackgroundBlock.prototype);
    PulseGrid.prototype.constructor = PulseGrid;

    PulseGrid.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var sp = this.simplified ? 22 : (SPACING[this.opts.intensity] || 16);
        var base = pal[0], pulse = pal[1];

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060610';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = 'rgba(' + base[0] + ',' + base[1] + ',' + base[2] + ',0.08)';
        ctx.lineWidth = 0.55;
        for (var x = 0; x < W; x += sp) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (var y = 0; y < H; y += sp) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

        var px = ((t * spd * 58) % W);
        var pg = ctx.createLinearGradient(px - 24, 0, px + 24, 0);
        pg.addColorStop(0,   'rgba(' + pulse[0] + ',' + pulse[1] + ',' + pulse[2] + ',0)');
        pg.addColorStop(0.5, 'rgba(' + pulse[0] + ',' + pulse[1] + ',' + pulse[2] + ',0.28)');
        pg.addColorStop(1,   'rgba(' + pulse[0] + ',' + pulse[1] + ',' + pulse[2] + ',0)');
        ctx.strokeStyle = pg;
        ctx.lineWidth = 1.4;
        for (var y2 = 0; y2 < H; y2 += sp) {
            ctx.beginPath(); ctx.moveTo(px - 30, y2); ctx.lineTo(px + 30, y2); ctx.stroke();
        }

        this.t += 0.016;
    };

    function initV2BgPulseGrid() {
        document.querySelectorAll('.v2-bg-pulse-grid').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new PulseGrid(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgPulseGrid); }
    else { initV2BgPulseGrid(); }
    BG_ANIMATIONS['pulse_grid'] = function(canvas, section) { new PulseGrid(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-isometric-lines
// ─────────────────────────────────────────────────────────────────────────────

(function () {
    var DEFAULTS = { palette: 'agency', intensity: 'medium', speed: 4, mobileMode: 'simplify' };
    var PALETTES = {
        agency: [[60,137,201],[118,35,120]],
        ocean:  [[6,182,212],[56,189,248]],
        ember:  [[239,68,68],[249,115,22]],
        mono:   [[80,100,160],[60,80,140]]
    };
    var SPACING = { low: 50, medium: 36, high: 24 };

    function IsometricLines(el) { BackgroundBlock.call(this, el, DEFAULTS); this.t = 0; }
    IsometricLines.prototype = Object.create(BackgroundBlock.prototype);
    IsometricLines.prototype.constructor = IsometricLines;

    IsometricLines.prototype.draw = function () {
        var ctx = this.ctx, W = this.w, H = this.h, t = this.t;
        var pal = PALETTES[this.opts.palette] || PALETTES.agency;
        var spd = BgUtils.clamp(this.opts.speed, 1, 10) / 10;
        var sp  = SPACING[this.opts.intensity] || 36;
        var off = (t * spd * 7) % sp;
        var extra = Math.ceil(H * 0.577 / sp) + 2;

        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#060610';
        ctx.fillRect(0, 0, W, H);

        ctx.strokeStyle = 'rgba(' + pal[0][0] + ',' + pal[0][1] + ',' + pal[0][2] + ',0.10)';
        ctx.lineWidth = 0.6;
        for (var i = -extra; i < W / sp + extra; i++) {
            ctx.beginPath();
            ctx.moveTo(i * sp + off, 0);
            ctx.lineTo(i * sp + H * 0.577 + off, H);
            ctx.stroke();
        }

        ctx.strokeStyle = 'rgba(' + pal[1][0] + ',' + pal[1][1] + ',' + pal[1][2] + ',0.08)';
        for (var i2 = -extra; i2 < W / sp + extra; i2++) {
            ctx.beginPath();
            ctx.moveTo(i2 * sp - off, 0);
            ctx.lineTo(i2 * sp - H * 0.577 - off, H);
            ctx.stroke();
        }
        this.t += 0.016;
    };

    function initV2BgIsometricLines() {
        document.querySelectorAll('.v2-bg-isometric-lines').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';
            new IsometricLines(el).init();
        });
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initV2BgIsometricLines); }
    else { initV2BgIsometricLines(); }
    BG_ANIMATIONS['isometric_lines'] = function(canvas, section) { new IsometricLines(section).init(); };
})();


// ─────────────────────────────────────────────────────────────────────────────
// v2-bg-scale-surface
// ─────────────────────────────────────────────────────────────────────────────

(function () {

    var PALETTES = {
        original: { atmoR:20,  atmoG:50,  atmoB:200, surfR:90,  surfG:10,  surfB:160, ridgeR:140,ridgeG:80, ridgeB:220, deepR:40,  deepG:5,   deepB:80  },
        agency:   { atmoR:60,  atmoG:137, atmoB:201, surfR:193, surfG:8,   surfB:125, ridgeR:200,ridgeG:100,ridgeB:180, deepR:80,  deepG:5,   deepB:100 },
        ember:    { atmoR:30,  atmoG:80,  atmoB:180, surfR:220, surfG:80,  surfB:20,  ridgeR:255,ridgeG:160,ridgeB:50,  deepR:100, deepG:20,  deepB:10  },
        teal:     { atmoR:10,  atmoG:100, atmoB:180, surfR:20,  surfG:160, surfB:180, ridgeR:80, ridgeG:200,ridgeB:220, deepR:10,  deepG:60,  deepB:100 }
    };

    var SPD  = { slow: 1.0, medium: 2.0, fast: 3.5 };
    var SZ   = { fine: 11,   medium: 17,  large: 26  };
    var AMP  = { subtle: 0.06, medium: 0.14, dramatic: 0.26 };

    var DEFAULTS = {
        palette:       'original',
        speed:         'slow',
        scaleSize:     'medium',
        waveAmplitude: 'medium',
        mobileMode:    'disable'
    };

    function ScaleSurface(el) {
        BackgroundBlock.call(this, el, DEFAULTS);
        this.opts.scaleSize     = el.dataset.bgScaleSize     || DEFAULTS.scaleSize;
        this.opts.waveAmplitude = el.dataset.bgWaveAmplitude || DEFAULTS.waveAmplitude;
        this.opts.speedKey      = el.dataset.bgSpeed         || DEFAULTS.speed;
        this.t = 0;
    }

    ScaleSurface.prototype = Object.create(BackgroundBlock.prototype);
    ScaleSurface.prototype.constructor = ScaleSurface;

    ScaleSurface.prototype.ridgeY = function (x, W, H) {
        var spd = SPD[this.opts.speedKey] || SPD.slow;
        var amp = AMP[this.opts.waveAmplitude] || AMP.medium;
        var baseY = H * 0.72 - (x / W) * H * 0.58;
        var wave  = Math.sin(x / W * Math.PI * 1.4 + this.t * spd * 0.8) * amp * H;
        return baseY + wave;
    };

    ScaleSurface.prototype.draw = function () {
        var ctx  = this.ctx;
        var W    = this.w;
        var H    = this.h;
        var p    = PALETTES[this.opts.palette] || PALETTES.original;
        var r    = this.simplified ? SZ.large : (SZ[this.opts.scaleSize] || SZ.medium);
        var self = this;

        ctx.fillStyle = '#030308';
        ctx.fillRect(0, 0, W, H);

        var rw   = r * 1.08;
        var rh   = r * 0.88;
        var cols = Math.ceil(W / rw) + 3;
        var rows = Math.ceil(H / rh) + 3;

        for (var row = -1; row < rows; row++) {
            for (var col = -1; col < cols; col++) {
                var stagger = (row % 2) * rw * 0.5;
                var sx = col * rw + stagger;
                var sy = row * rh;

                var ry = self.ridgeY(sx, W, H);
                var belowRidge = sy - ry;

                if (belowRidge < -r * 0.8) continue;
                if (belowRidge > H * 0.85)  continue;

                var depth       = Math.min(1, Math.max(0, belowRidge / (H * 0.7)));
                var ridgeFactor = Math.max(0, 1 - belowRidge / (r * 3));
                var ridgeBright = Math.pow(ridgeFactor, 1.8);
                var leftLight   = Math.max(0, 1 - sx / W) * 0.9 * ridgeBright;
                var rightLight  = (sx / W) * 0.7 * (1 - depth * 0.4);
                var ridgeHL     = ridgeBright * 0.6;

                var cr = p.deepR * (1 - ridgeBright) + (p.ridgeR * ridgeHL + p.atmoR * leftLight * 1.5 + p.surfR * rightLight);
                var cg = p.deepG * (1 - ridgeBright) + (p.ridgeG * ridgeHL + p.atmoG * leftLight * 1.5 + p.surfG * rightLight);
                var cb = p.deepB * (1 - ridgeBright) + (p.ridgeB * ridgeHL + p.atmoB * leftLight * 1.5 + p.surfB * rightLight);

                cr = Math.min(255, Math.max(0, cr));
                cg = Math.min(255, Math.max(0, cg));
                cb = Math.min(255, Math.max(0, cb));

                var alpha = 0.55 + depth * 0.4 + ridgeBright * 0.05;
                if (belowRidge < 0) {
                    alpha = Math.max(0, (belowRidge + r * 0.8) / (r * 0.8)) * 0.6;
                }

                var rx2 = r * 0.44;
                var ry2 = r * 0.35;

                ctx.save();
                ctx.translate(sx, sy);

                var grad = ctx.createRadialGradient(-rx2 * 0.25, -ry2 * 0.28, 0, 0, 0, r * 0.52);
                grad.addColorStop(0,   'rgba(' + Math.round(Math.min(255, cr + 40)) + ',' + Math.round(Math.min(255, cg + 35)) + ',' + Math.round(Math.min(255, cb + 50)) + ',' + alpha + ')');
                grad.addColorStop(0.6, 'rgba(' + Math.round(cr) + ',' + Math.round(cg) + ',' + Math.round(cb) + ',' + (alpha * 0.9) + ')');
                grad.addColorStop(1,   'rgba(' + Math.round(cr * 0.5) + ',' + Math.round(cg * 0.5) + ',' + Math.round(cb * 0.5) + ',' + (alpha * 0.7) + ')');

                ctx.beginPath();
                ctx.ellipse(0, 0, rx2, ry2, 0, 0, Math.PI * 2);
                ctx.fillStyle = grad;
                ctx.fill();

                ctx.beginPath();
                ctx.ellipse(0, 0, rx2, ry2, 0, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(0,0,0,' + (0.5 + depth * 0.3) + ')';
                ctx.lineWidth = 0.7;
                ctx.stroke();

                ctx.restore();
            }
        }

        // Atmospheric effects
        var atmo1 = ctx.createRadialGradient(0, H * 0.38, 0, 0, H * 0.38, W * 0.52);
        atmo1.addColorStop(0,   'rgba(' + p.atmoR + ',' + p.atmoG + ',' + p.atmoB + ',0.28)');
        atmo1.addColorStop(0.5, 'rgba(' + p.atmoR + ',' + p.atmoG + ',' + p.atmoB + ',0.10)');
        atmo1.addColorStop(1,   'rgba(' + p.atmoR + ',' + p.atmoG + ',' + p.atmoB + ',0)');
        ctx.fillStyle = atmo1;
        ctx.fillRect(0, 0, W, H);

        var atmo2 = ctx.createRadialGradient(W * 0.12, H * 0.22, 0, W * 0.12, H * 0.22, W * 0.38);
        atmo2.addColorStop(0, 'rgba(' + p.atmoR + ',' + p.atmoG + ',' + p.atmoB + ',0.18)');
        atmo2.addColorStop(1, 'rgba(' + p.atmoR + ',' + p.atmoG + ',' + p.atmoB + ',0)');
        ctx.fillStyle = atmo2;
        ctx.fillRect(0, 0, W, H);

        var step = this.simplified ? 20 : 8;
        for (var xg = 0; xg < W; xg += step) {
            var ryg = self.ridgeY(xg, W, H);
            var grd = ctx.createRadialGradient(xg, ryg, 0, xg, ryg, r * 2.5);
            grd.addColorStop(0, 'rgba(' + p.ridgeR + ',' + p.ridgeG + ',' + p.ridgeB + ',0.12)');
            grd.addColorStop(1, 'rgba(' + p.ridgeR + ',' + p.ridgeG + ',' + p.ridgeB + ',0)');
            ctx.fillStyle = grd;
            ctx.fillRect(xg - r * 3, ryg - r * 3, r * 6, r * 6);
        }

        var vig = ctx.createRadialGradient(W * 0.5, H * 0.5, H * 0.25, W * 0.5, H * 0.5, H * 0.85);
        vig.addColorStop(0, 'rgba(0,0,0,0)');
        vig.addColorStop(1, 'rgba(0,0,0,0.6)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, W, H);

        var corner = ctx.createRadialGradient(0, 0, 0, 0, 0, H * 0.4);
        corner.addColorStop(0, 'rgba(0,0,0,0.7)');
        corner.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = corner;
        ctx.fillRect(0, 0, W, H);

        this.t += 0.016;
    };

    function initV2BgScaleSurface() {
        document.querySelectorAll('.v2-bg-scale-surface').forEach(function (el) {
            if (el.dataset.bgInit === 'true') return;
            el.dataset.bgInit = 'true';

            if (BgUtils.prefersReducedMotion()) {
                var cv = el.querySelector('canvas');
                if (cv) cv.style.display = 'none';
                return;
            }

            var mob = el.dataset.bgMobile || 'disable';
            if (BgUtils.isMobile() && mob === 'disable') {
                var cv2 = el.querySelector('canvas');
                if (cv2) cv2.style.display = 'none';
                return;
            }

            var inst = new ScaleSurface(el);
            inst.simplified = BgUtils.isMobile() && mob === 'simplify';
            inst.init();
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initV2BgScaleSurface);
    } else {
        initV2BgScaleSurface();
    }

})();
